import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useNotificationSummary from '../../hooks/useNotificationSummary';

function Header({
  variant = 'auth',
  activeHref,
  authenticated,
  accountHref,
  accountLabel,
  accountIcon,
  hasUnreadMessages = false,
  className = ''
}) {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const burgerRef = useRef(null);

  const isAuthLayout = variant === 'auth';
  const isAuthenticated = authenticated ?? Boolean(localStorage.getItem('token'));
  const currentHref = activeHref === null ? '' : (activeHref || location.pathname);
  const resolvedAccountHref = accountHref || (isAuthenticated ? '/profile' : '/log_in');
  const resolvedAccountLabel = accountLabel || (isAuthenticated ? 'Личный кабинет' : 'Вход');
  const resolvedAccountIcon = accountIcon || (isAuthenticated ? 'circle' : 'person');
  const isAccountActive = currentHref === resolvedAccountHref;
  const { hasUnreadMessages: hasUnreadMessagesFromNotifications, hasSympathyNotifications } = useNotificationSummary();
  const showChatNotifications = hasUnreadMessages || hasUnreadMessagesFromNotifications;
  const resolveGuestHref = (href) => (isAuthenticated || href === '/' ? href : '/log_in');

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (menuRef.current?.contains(target) || burgerRef.current?.contains(target)) {
        return;
      }
      setIsMenuOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { href: '/', label: 'Главная' },
    { href: '/chats', label: 'Чаты', accentDot: true },
    { href: '/feed', label: 'Лента' },
    { href: '/sympathies', label: 'Симпатии', accentDot: true },
    { href: '/search', label: 'Поиск' },
  ];

  const headerClassName = [
    'App-Header',
    'app-header',
    'relative',
    'z-50',
    'bg-[var(--header-shell-bg)]',
    isAuthLayout ? 'app-header--auth' : '',
    isAuthenticated ? 'app-header--authenticated' : 'app-header--guest',
    className
  ].filter(Boolean).join(' ');

  const getLinkDotState = (href) => {
    if (href === '/chats') return showChatNotifications;
    if (href === '/sympathies') return hasSympathyNotifications;
    return false;
  };

  return (
    <header className={headerClassName}>
      <div className="layout-12 mx-auto app-header__frame">
        <div className={`app-header__row h-full${isAuthLayout ? ' app-header__row--auth' : ''}`}>
          <div className={`app-header__main flex-1 basis-0 min-w-0 flex items-center justify-between${isAuthLayout ? ' app-header__main--auth' : ''}`}>
            <div className="app-header__mobile-brand flex items-center md:hidden">
              <button
                ref={burgerRef}
                onClick={() => setIsMenuOpen((prev) => !prev)}
                className="text-[var(--header-text)] focus:outline-none flex items-center justify-center app-header__burger"
                aria-label="Открыть меню"
                type="button"
              >
                <svg width="13" height="10" viewBox="0 0 13 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.4583 10C12.602 10 12.7398 9.94732 12.8413 9.85355C12.9429 9.75979 13 9.63261 13 9.5C13 9.36739 12.9429 9.24021 12.8413 9.14645C12.7398 9.05268 12.602 9 12.4583 9L0.541665 9C0.398007 9 0.260231 9.05268 0.158649 9.14645C0.0570673 9.24021 3.57628e-07 9.36739 3.57628e-07 9.5C3.57628e-07 9.63261 0.0570673 9.75979 0.158649 9.85355C0.260231 9.94732 0.398007 10 0.541665 10L12.4583 10ZM7.04167 7C7.18533 7 7.3231 6.94732 7.42468 6.85355C7.52626 6.75978 7.58333 6.63261 7.58333 6.5C7.58333 6.36739 7.52626 6.24022 7.42468 6.14645C7.3231 6.05268 7.18533 6 7.04167 6L0.541665 6C0.398007 6 0.260231 6.05268 0.158649 6.14645C0.0570673 6.24022 3.57628e-07 6.36739 3.57628e-07 6.5C3.57628e-07 6.63261 0.0570673 6.75978 0.158649 6.85355C0.260231 6.94732 0.398007 7 0.541665 7L7.04167 7ZM7.04167 4C7.18533 4 7.3231 3.94732 7.42468 3.85355C7.52626 3.75979 7.58333 3.63261 7.58333 3.5C7.58333 3.36739 7.52626 3.24021 7.42468 3.14645C7.3231 3.05268 7.18533 3 7.04167 3L0.541665 3C0.398007 3 0.260231 3.05268 0.158649 3.14645C0.0570673 3.24021 3.57628e-07 3.36739 3.57628e-07 3.5C3.57628e-07 3.63261 0.0570673 3.75979 0.158649 3.85355C0.260231 3.94732 0.398007 4 0.541665 4L7.04167 4ZM12.4583 1C12.602 1 12.7398 0.947322 12.8413 0.853554C12.9429 0.759786 13 0.632607 13 0.5C13 0.367391 12.9429 0.240215 12.8413 0.146447C12.7398 0.0526788 12.602 -2.38418e-07 12.4583 -2.38418e-07L0.541665 -2.38418e-07C0.398007 -2.38418e-07 0.260231 0.0526788 0.158649 0.146447C0.0570673 0.240215 3.57628e-07 0.367391 3.57628e-07 0.5C3.57628e-07 0.632607 0.0570673 0.759786 0.158649 0.853554C0.260231 0.947322 0.398007 1 0.541665 1L12.4583 1Z" fill="var(--header-text)" />
                </svg>
              </button>
              <Link to="/" className={`header-logo header-logo--mobile whitespace-nowrap${isAuthLayout ? ' header-logo--auth' : ''}`}>
                <span className="header-logo__accent">T</span>
                <span className="header-logo__text whitespace-nowrap">-Interest</span>
              </Link>
            </div>

            <Link to="/" className={`header-logo header-logo--desktop hidden md:inline-flex whitespace-nowrap${isAuthLayout ? ' header-logo--auth' : ''}`}>
              <span className="header-logo__accent">T</span>
              <span className="header-logo__text whitespace-nowrap">-Interest</span>
            </Link>

            <nav className={`hidden md:flex items-center app-header__nav${isAuthLayout ? ' app-header__nav--auth' : ''}`}>
              {navLinks.map((link) => (
                <span key={link.href} className="relative flex items-center">
                  <Link
                    className={`header-nav-link ${link.href === currentHref ? 'header-nav-link--active' : ''}${isAuthLayout ? ' header-nav-link--auth' : ''}`}
                    to={resolveGuestHref(link.href)}
                  >
                    <span className="header-nav-link__label">
                      <span>{link.label}</span>
                      {getLinkDotState(link.href) && <span className="header-nav-link__accent-dot" aria-hidden />}
                    </span>
                  </Link>
                </span>
              ))}
            </nav>
          </div>

          <Link
            to={resolvedAccountHref}
            className={`app-header__login flex-none flex justify-center items-center${isAuthLayout ? ' app-header__login--auth' : ''}${isAccountActive ? ' app-header__login--active' : ''}`}
          >
            <div className={`flex items-center justify-center app-header__login-inner w-full text-[var(--header-text)]${isAuthLayout ? ' app-header__login-inner--auth' : ''}`}>
              {resolvedAccountIcon === 'circle' ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="bi bi-person-circle app-header__icon shrink-0" viewBox="0 0 16 16" aria-hidden>
                  <path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0" />
                  <path fillRule="evenodd" d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8m8-7a7 7 0 0 0-5.468 11.37C3.242 11.226 4.805 10 8 10s4.757 1.225 5.468 2.37A7 7 0 0 0 8 1" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="bi bi-person app-header__icon shrink-0" viewBox="2 2 12 12" aria-hidden>
                  <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6m2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0m4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4m-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10s-3.516.68-4.168 1.332c-.678.678-.83 1.418-.832 1.664z" />
                </svg>
              )}
              <span className={`app-header__login-text text-[var(--header-text)] font-tt-norms font-medium leading-none${isAuthLayout ? ' app-header__login-text--auth' : ''}`}>{resolvedAccountLabel}</span>
            </div>
          </Link>
        </div>
      </div>

      {isMenuOpen && (
        <div
          ref={menuRef}
          className="absolute top-full left-0 w-full z-50 md:hidden"
          style={{ backgroundColor: 'var(--header-menu-bg)', boxShadow: `0 4px 12px var(--header-menu-shadow)` }}
        >
          <div className="layout-12 mx-auto py-4 pl-4 pr-4 flex flex-col gap-4">
            {navLinks.slice(1).map((link) => (
              <span key={link.href} className="relative flex w-fit items-center">
                <Link
                  onClick={() => setIsMenuOpen(false)}
                  className={`text-[clamp(1rem,4vw,1.125rem)] font-tt-norms py-2 ${link.href === currentHref ? 'font-bold' : 'font-medium'}`}
                  style={{ color: link.href === currentHref ? 'var(--header-mobile-link-active)' : 'var(--header-mobile-link)' }}
                  to={resolveGuestHref(link.href)}
                >
                  <span className="header-nav-link__label">
                    <span>{link.label}</span>
                    {getLinkDotState(link.href) && <span className="header-nav-link__accent-dot" aria-hidden />}
                  </span>
                </Link>
              </span>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
